import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useCall } from "../context/CallContext";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const PhoneIcon = ({ color = "white" }: { color?: string }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const HangupIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1a1 1 0 0 1-.68.95c-.9.3-1.75.7-2.53 1.19a1 1 0 0 1-1.21-.15l-2.13-2.13a1 1 0 0 1 .12-1.51A17.9 17.9 0 0 1 12 7c4.24 0 8.13 1.44 11.03 3.87a1 1 0 0 1 .12 1.51l-2.13 2.13a1 1 0 0 1-1.21.15 15.6 15.6 0 0 0-2.53-1.19 1 1 0 0 1-.68-.95v-3.1A17.7 17.7 0 0 0 12 9z"/></svg>
);
const MicIcon = ({ off }: { off?: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v1a7 7 0 0 0 14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
    {off && <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2.5"/>}
  </svg>
);

const fmtDuration = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

export default function CallOverlay() {
  const { state, localStream, remoteStream, muted, acceptCall, rejectCall, endCall, toggleMute } = useCall();
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
  }, [localStream]);
  useEffect(() => {
    if (state.status === "connected" && state.callType === "video" && remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = remoteStream;
  }, [remoteStream, state]);

  useEffect(() => {
    if (state.status !== "connected") { setDuration(0); return; }
    const iv = setInterval(() => setDuration(Math.round((Date.now() - state.startedAt) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [state]);

  if (state.status === "idle") return null;

  const isVideo = state.callType === "video";

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "#0a0a0a", zIndex: 5000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "white" }}>
      <audio ref={remoteAudioRef} autoPlay />

      {isVideo && state.status === "connected" && (
        <video ref={remoteVideoRef} autoPlay playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#111" }} />
      )}
      {isVideo && localStream && (
        <video ref={localVideoRef} autoPlay playsInline muted style={{ position: "absolute", bottom: 110, right: 20, width: 110, height: 150, borderRadius: 14, objectFit: "cover", border: "2px solid white", zIndex: 2 }} />
      )}

      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: (isVideo && state.status === "connected") ? "auto" : 0, marginTop: (isVideo && state.status === "connected") ? 60 : 0 }}>
        {!(isVideo && state.status === "connected") && (
          <div style={{ width: 96, height: 96, borderRadius: "50%", backgroundColor: "#333", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, fontWeight: 700, overflow: "hidden", marginBottom: 8 }}>
            {state.peer.avatar ? <img src={state.peer.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(state.peer.name)}
          </div>
        )}
        <div style={{ fontSize: 20, fontWeight: 700, textShadow: isVideo ? "0 1px 6px rgba(0,0,0,0.6)" : "none" }}>{state.peer.name}</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", textShadow: isVideo ? "0 1px 6px rgba(0,0,0,0.6)" : "none" }}>
          {state.status === "incoming" && `${isVideo ? "Appel vidéo" : "Appel"} entrant...`}
          {state.status === "outgoing" && "Appel en cours..."}
          {state.status === "connected" && fmtDuration(duration)}
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1, display: "flex", gap: 24, marginTop: 50, marginBottom: 60 }}>
        {state.status === "incoming" ? (
          <>
            <button onClick={rejectCall} style={{ width: 60, height: 60, borderRadius: "50%", border: "none", backgroundColor: "#c0392b", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><HangupIcon /></button>
            <button onClick={acceptCall} style={{ width: 60, height: 60, borderRadius: "50%", border: "none", backgroundColor: "#2ecc71", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><PhoneIcon /></button>
          </>
        ) : (
          <>
            {state.status === "connected" && (
              <button onClick={toggleMute} style={{ width: 54, height: 54, borderRadius: "50%", border: "none", backgroundColor: muted ? "white" : "rgba(255,255,255,0.2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MicIcon off={muted} />
              </button>
            )}
            <button onClick={endCall} style={{ width: 60, height: 60, borderRadius: "50%", border: "none", backgroundColor: "#c0392b", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><HangupIcon /></button>
          </>
        )}
      </div>
    </div>
  ,
    document.body
  );
}
